import React, {useEffect, useState} from 'react';
import {Link, useLocation, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';


const STATUS_COLORS = {
  WAITING_TO_SHELF: 'bg-yellow-100 text-yellow-800',
  ON_SHELF: 'bg-green-100 text-green-800',
  PICKED: 'bg-blue-100 text-blue-800',
  PENDING_SHIPPING: 'bg-orange-100 text-orange-800',
  SHIPPED: 'bg-purple-100 text-purple-800',
  DISCARDED: 'bg-red-100 text-red-800',
};

const AVAILABILITY_COLORS = {
  AVAILABLE: 'bg-green-100 text-green-800',
  ON_HOLD: 'bg-yellow-100 text-yellow-800',
  SOLD: 'bg-blue-100 text-blue-800',
  BACKORDERED: 'bg-orange-100 text-orange-800',
  SPOILED: 'bg-red-100 text-red-800',
};

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [item, setItem] = useState(null);
  const [inventoryList, setInventoryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(location.state?.saved || false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddQty, setQuickAddQty] = useState(1);
  const [quickAddSize, setQuickAddSize] = useState('');
  const [quickAddUnitId, setQuickAddUnitId] = useState('');
  const [quickAddAvailability, setQuickAddAvailability] = useState('AVAILABLE');
  const [units, setUnits] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (saved) {
      const timer = setTimeout(() => setSaved(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [saved]);

  const fetchData = () => {
    Promise.all([
      api(`/items/${id}`).then(res => res.ok ? res.json() : null),
      api(`/items/${id}/inventory`).then(res => res.ok ? res.json() : []),
      api(`/units`).then(res => res.ok ? res.json() : []),
    ]).then(([itemData, inventoryData, unitsData]) => {
      setItem(itemData);
      setInventoryList(Array.isArray(inventoryData) ? inventoryData : []);
      setUnits(Array.isArray(unitsData) ? unitsData : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const confirmDelete = () => {
    if (deleteTarget === 'item') {
      api(`/items/${id}`, { method: 'DELETE' })
        .then(() => navigate('/inventory/items'));
    } else {
      api(`/items/${id}/inventory/${deleteTarget}`, { method: 'DELETE' })
        .then(() => { setDeleteTarget(null); fetchData(); });
    }
  };

  const handleQuickAdd = (e) => {
    e.preventDefault();
    api(`/items/${id}/inventory/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantity: quickAddQty, size: quickAddSize || undefined, unitId: quickAddUnitId || undefined, availability: quickAddAvailability || undefined }),
    }).then(() => {
      setShowQuickAdd(false);
      setQuickAddQty(1);
      setQuickAddSize('');
      setQuickAddUnitId('');
      setQuickAddAvailability('AVAILABLE');
      fetchData();
    });
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!item) return <div className="p-6">Item not found.</div>;

  return (
    <Layout>
          {saved && (
            <div className="mb-4 px-4 py-3 bg-green-100 text-green-800 rounded-lg">Item saved successfully.</div>
          )}
          <Link to="/inventory/items" className="text-red-600 hover:text-red-800 mb-4 inline-block">&larr; Back to Items</Link>
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <h1 className="text-2xl font-bold text-gray-900">{item.name}</h1>
              <div className="flex gap-3">
                <Link to={`/inventory/items/${id}/edit`} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Edit
                </Link>
                <button onClick={() => setDeleteTarget('item')} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                  Delete
                </button>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-500">Description</label>
              <p className="mt-1 text-gray-900">{item.description || '—'}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-500">SKU</label>
              <p className="mt-1 text-gray-900">{item.sku || '—'}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-500">Barcode</label>
              <p className="mt-1 text-gray-900">{item.barcode || '—'}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-500">Created</label>
              <p className="mt-1 text-gray-900">{new Date(item.createdAt).toLocaleString()}</p>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-500">Updated</label>
              <p className="mt-1 text-gray-900">{new Date(item.updatedAt).toLocaleString()}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <h2 className="text-xl font-bold text-gray-900">Inventory ({inventoryList.length})</h2>
                <div className="flex gap-3">
                  <button onClick={() => setShowQuickAdd(true)} className="px-4 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50">
                    Quick Add
                  </button>
                  <Link to={`/inventory/items/${id}/inventory/new`} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                    Add Inventory
                  </Link>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shelf Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Availability</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inventoryList.map((inv) => (
                    <tr key={inv.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{inv.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">{inv.size || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">{inv.unitName || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">{inv.shelfLocation || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[inv.status] || 'bg-gray-100 text-gray-800'}`}>
                          {inv.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {inv.availability ? (
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${AVAILABILITY_COLORS[inv.availability] || 'bg-gray-100 text-gray-800'}`}>
                            {inv.availability.replace(/_/g, ' ')}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {new Date(inv.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/inventory/items/${id}/inventory/${inv.id}/edit`} className="text-red-600 hover:text-red-800 font-medium">Edit</Link>
                        <button onClick={() => setDeleteTarget(inv.id)} className="ml-3 text-gray-500 hover:text-red-600 font-medium">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {inventoryList.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No inventory entries yet.
                </div>
              )}
            </div>
          </div>

          {deleteTarget && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 w-96">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Delete</h3>
                <p className="text-gray-600 mb-6">
                  {deleteTarget === 'item'
                    ? 'Are you sure you want to delete this item? This will also delete all inventory entries.'
                    : 'Are you sure you want to delete this inventory entry?'}
                </p>
                <div className="flex justify-end gap-3">
                  <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                    Cancel
                  </button>
                  <button onClick={confirmDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {showQuickAdd && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 w-96">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Add Inventory</h3>
                <form onSubmit={handleQuickAdd}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                    <input
                      type="number"
                      min="1"
                      required
                      value={quickAddQty}
                      onChange={(e) => setQuickAddQty(parseInt(e.target.value) || 1)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                    <p className="mt-1 text-sm text-gray-500">Creates entries with "Waiting to Shelf" status.</p>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                    <input
                      type="text"
                      value={quickAddSize}
                      onChange={(e) => setQuickAddSize(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                    <select
                      value={quickAddUnitId}
                      onChange={(e) => setQuickAddUnitId(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    >
                      <option value="">— None —</option>
                      {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Availability</label>
                    <select
                      value={quickAddAvailability}
                      onChange={(e) => setQuickAddAvailability(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    >
                      {['AVAILABLE','ON_HOLD','SOLD','BACKORDERED','SPOILED'].map(a => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
                    </select>
                  </div>
                  <div className="flex justify-end gap-3">
                    <button type="button" onClick={() => setShowQuickAdd(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                      Add
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
    </Layout>
  );
}
